// Draws a scene of an AI-generated template into a picture: the live preview while it is being
// generated, and the slide thumbnails saved with the project (so My Projects shows it straight
// away, without opening the editor). Every layer is drawn in its final position.

import { W, H, wrap, SLIDE_PX, restingPlace } from "./aiShapes";
import { assetUrl } from "../assetUrl";
import Lottie from "lottie-web";

const imageCache = new Map(); // url -> Promise<ImageBitmap | HTMLImageElement>

function loadSvg(markup, w, h) {
  // Give the SVG its real size, so it is drawn sharp (not a small picture stretched).
  const sized = markup.replace('width="100%" height="100%"', `width="${Math.max(1, Math.round(w))}" height="${Math.max(1, Math.round(h))}"`);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(sized);
  });
}

// Library pictures come from the API. Loaded as data (not as an <img>), so drawing them does not
// block saving the picture; "no-store" avoids an earlier cached copy without the CORS header.
// SVG pictures (props) are drawn from their text, other pictures as bitmaps.
function loadPicture(url) {
  if (!imageCache.has(url)) {
    const p = fetch(url, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.blob();
      })
      .then(async (blob) => {
        if ((blob.type || "").includes("svg") || /\.svg($|\?)/i.test(url)) {
          const text = await blob.text();
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(text);
          });
        }
        return createImageBitmap(blob);
      });
    p.catch(() => imageCache.delete(url));
    imageCache.set(url, p);
  }
  return imageCache.get(url);
}

// A repainted character (aiRecolor) has no library picture in its colours: its animation is drawn
// instead (first frame, as SVG, at the layer's size).
async function animationPicture(animationData, w, h) {
  const box = document.createElement("div");
  box.style.cssText = `position:fixed;left:-10000px;top:0;width:${Math.round(w)}px;height:${Math.round(h)}px;overflow:hidden;pointer-events:none;`;
  document.body.appendChild(box);
  let anim = null;
  try {
    anim = Lottie.loadAnimation({
      container: box,
      renderer: "svg",
      loop: false,
      autoplay: false,
      animationData: JSON.parse(JSON.stringify(animationData)),
      rendererSettings: { preserveAspectRatio: "xMidYMid meet" },
    });
    await new Promise((resolve) => {
      if (anim.isLoaded) return resolve();
      anim.addEventListener("DOMLoaded", resolve);
      setTimeout(resolve, 3000);
    });
    anim.goToAndStop(0, true);
    const svg = box.querySelector("svg");
    if (!svg) throw new Error("no svg");
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svg.setAttribute("width", String(Math.max(1, Math.round(w))));
    svg.setAttribute("height", String(Math.max(1, Math.round(h))));
    const markup = new XMLSerializer().serializeToString(svg);
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(markup);
    });
  } finally {
    if (anim) anim.destroy();
    box.remove();
  }
}

function drawText(ctx, layer, s) {
  const size = layer.fontSize;
  const weight = layer.fontWeight || "400";
  const ls = Number(layer.LetterSpacing) || 0;
  const lh = Number(layer.LineHeight) || 1.15;
  const lines = wrap(layer.text, layer.width, size, weight, ls);
  ctx.save();
  ctx.globalAlpha = layer.opacity ?? 1;
  ctx.fillStyle = layer.textColor || "#000";
  ctx.font = `${weight} ${size * s}px Poppins, sans-serif`;
  if ("letterSpacing" in ctx) ctx.letterSpacing = `${ls * size * s}px`;
  ctx.textBaseline = "middle";
  const align = layer.Align === "center" ? "center" : layer.Align === "right" ? "right" : "left";
  ctx.textAlign = align;
  const x = align === "center" ? layer.x + layer.width / 2 : align === "right" ? layer.x + layer.width : layer.x;
  lines.forEach((line, i) => {
    const y = layer.y + 2 + (i + 0.5) * size * lh;
    ctx.fillText(line, x * s, y * s);
  });
  ctx.restore();
}

/**
 * Picture of the slide as a data URL (JPEG). width: size of the picture's longer side in pixels.
 * Layers that cannot be drawn (e.g. a picture that does not load) are left out.
 */
export async function renderSlide(slide, { apiBaseURL, width = 960, quality = 0.86 } = {}) {
  // (width is the longer side: a mobile scene is 540 x 960.)
  const s = width / Math.max(W, H);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(W * s);
  canvas.height = Math.round(H * s);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Library background (an animated one is drawn from its preview picture), covering the canvas.
  const bg = slide.SlideBackground;
  if (bg && bg.Path && (bg.type === "image" || bg.type === "Animated")) {
    try {
      const img = await loadPicture(assetUrl(apiBaseURL, bg.Path));
      const iw = img.width || img.naturalWidth;
      const ih = img.height || img.naturalHeight;
      const k = Math.max(canvas.width / iw, canvas.height / ih);
      ctx.drawImage(img, (canvas.width - iw * k) / 2, (canvas.height - ih * k) / 2, iw * k, ih * k);
    } catch (e) {
      console.log("AI template preview: background could not be drawn.", e);
    }
  }

  // Load everything first, then draw in layer order. Layers are drawn where they are at the end of
  // the scene: a layer with an A to Z move at its end place (Z), and layers that are gone by then
  // (a presenter walking in) are left out.
  const layers = (slide.LayerData || [])
    .filter((l) => (Number(l.startPositionOnTimeline) || 0) + (Number(l.widthOnTimeline) || SLIDE_PX) >= SLIDE_PX - 1)
    .map((l) => {
      if (!l.Motion) return l;
      const at = restingPlace(l);
      const moved = Object.create(l); // keeps the non-saved flags (_recolored)
      moved.x = at.x;
      moved.y = at.y;
      return moved;
    });
  const loaded = await Promise.all(
    layers.map(async (l) => {
      try {
        if (l.type === "basicShape" && l.svgContent) return await loadSvg(l.svgContent, l.width * s, l.height * s);
        if (l.type === "Character" && l._recolored && l.JSONValue) {
          try {
            return await animationPicture(l.JSONValue, l.width * s, l.height * s);
          } catch (e) {
            console.log("AI template preview: repainted character drawn from its library picture.", e);
          }
        }
        if (l.type === "Character" && l.ImageSrc) return await loadPicture(assetUrl(apiBaseURL, l.ImageSrc));
        if (l.type === "Props" && l.PropImage) return await loadPicture(assetUrl(apiBaseURL, l.PropImage));
        if (l.type === "Shape" && l.ShapePath) return await loadPicture(assetUrl(apiBaseURL, l.ShapePath));
        if (l.type === "image" && l.ImageSrc) return await loadPicture(assetUrl(apiBaseURL, l.ImageSrc));
      } catch (e) {
        console.log("AI template preview: a layer could not be drawn.", l.Name, e);
      }
      return null;
    })
  );

  layers.forEach((l, i) => {
    if (l.type === "text") return drawText(ctx, l, s);
    const img = loaded[i];
    if (!img) return;
    ctx.save();
    ctx.globalAlpha = l.opacity ?? 1;
    if (l.type === "Character" && l.flip === "scaleX(-1)") {
      ctx.translate((l.x + l.width) * s, l.y * s);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0, l.width * s, l.height * s);
    } else {
      ctx.drawImage(img, l.x * s, l.y * s, l.width * s, l.height * s);
    }
    ctx.restore();
  });

  return canvas.toDataURL("image/jpeg", quality);
}
